import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { FaDumbbell, FaBiking, FaRunning, FaFire } from "react-icons/fa";
import { GiJumpingRope, GiAbdominalArmor } from "react-icons/gi";

const Aulas = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const classes = [
    {
      name: 'Aula Funcional',
      icon: FaDumbbell,
      schedule: 'Segunda e Sexta',
      times: ['07:15 - 08:00'],
      color: '#ff6b6b',
      delay: '0s'
    },
    {
      name: 'Aula Funcional',
      icon: FaDumbbell,
      schedule: 'Segunda e Quarta',
      times: ['19:00 - 19:45'],
      color: '#ff6b6b',
      delay: '0.1s'
    },
    {
      name: 'Aula Funcional',
      icon: FaDumbbell,
      schedule: 'Sexta',
      times: ['18:00 - 18:45'],
      color: '#ff6b6b',
      delay: '0.2s'
    },
    {
      name: 'GAP Abdominal',
      icon: GiAbdominalArmor,
      schedule: 'Quarta',
      times: ['07:15 - 08:00'],
      color: '#ffd700',
      delay: '0.3s'
    },
    {
      name: 'GAP Abdominal',
      icon: GiAbdominalArmor,
      schedule: 'Terça e Quinta',
      times: ['19:00 - 19:30'],
      color: '#ffd700',
      delay: '0.4s'
    },
    {
      name: 'Spinning',
      icon: FaBiking,
      schedule: 'Terça e Quinta',
      times: ['07:00 - 07:30', '07:30 - 08:00'],
      color: '#e5a800',
      delay: '0.5s'
    },
    {
      name: 'Spinning',
      icon: FaBiking,
      schedule: 'Terça e Quinta',
      times: ['19:00 - 19:30', '19:30 - 20:00'],
      color: '#e5a800',
      delay: '0.6s'
    },
    {
      name: 'Jump',
      icon: GiJumpingRope,
      schedule: 'Terça e Quinta',
      times: ['20:15 - 21:00'],
      color: '#f9ca24',
      delay: '0.7s'
    }
  ];

  return (
    <Container id="aulas" ref={sectionRef}>
      <BackgroundPattern />
      
      <Content>
        <HeaderSection $isVisible={isVisible}>
          <SectionBadge>
            <span>🏋️ AULAS COLETIVAS</span>
          </SectionBadge>
          <Title>
            TREINOS EM <HighlightText>GRUPO</HighlightText>
          </Title>
          <Subtitle>
            Motivação coletiva para resultados extraordinários. Confira nossa grade de aulas.
          </Subtitle>
        </HeaderSection>

        <ClassesGrid>
          {classes.map((cls, index) => (
            <ClassCard 
              key={index}
              $delay={cls.delay}
              $isVisible={isVisible}
              $color={cls.color}
            >
              <CardGlow $color={cls.color} />
              <CardContent>
                <IconWrapper $color={cls.color}>
                  <cls.icon />
                </IconWrapper>
                <ClassName>{cls.name}</ClassName>
                <Schedule>{cls.schedule}</Schedule>
                <TimesContainer>
                  {cls.times.map((time, tIndex) => (
                    <TimeTag key={tIndex}>
                      <FaFire className="fire-icon" />
                      {time}
                    </TimeTag>
                  ))}
                </TimesContainer>
              </CardContent>
              <CardBorder $color={cls.color} />
            </ClassCard>
          ))}
        </ClassesGrid>

        <CTASection $isVisible={isVisible}>
          <CTAText>Quer participar das nossas aulas?</CTAText>
          <CTAButton href="https://wa.me/5531971934978" target="_blank">
            <ButtonShine />
            <span>AGENDE AGORA</span>
          </CTAButton>
        </CTASection>
      </Content>
    </Container>
  );
};

export default Aulas;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const shine = keyframes`
  0% {
    left: -100%;
  }
  50%, 100% {
    left: 100%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const Container = styled.section`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-dark);
  position: relative;
  padding: 60px 0;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 20% 30%, rgba(229, 168, 0, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(255, 107, 107, 0.03) 0%, transparent 50%);
  pointer-events: none;
`;

const Content = styled.div`
  width: 90%;
  max-width: 1200px;
  position: relative;
  z-index: 1;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease forwards;
  `}
`;

const SectionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(229, 168, 0, 0.1);
  border: 1px solid rgba(229, 168, 0, 0.2);
  border-radius: 50px;
  padding: 10px 25px;
  margin-bottom: 25px;

  span {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--primary-color);
    letter-spacing: 3px;
  }
`;

const Title = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 15px;
`;

const HighlightText = styled.span`
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.1rem;
  color: #888;
  max-width: 500px;
  margin: 0 auto;
`;

const ClassesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 60px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ClassCard = styled.div`
  position: relative;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  overflow: hidden;
  opacity: 0;
  transition: all 0.4s ease;
  
  ${({ $isVisible, $delay }) => $isVisible && css`
    animation: ${scaleIn} 0.6s ease forwards;
    animation-delay: ${$delay};
  `}

  &:hover {
    transform: translateY(-10px);
    border-color: ${({ $color }) => $color || 'var(--primary-color)'};
  }
`;

const CardGlow = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 150px;
  height: 150px;
  background: ${({ $color }) => $color || 'var(--primary-color)'};
  filter: blur(80px);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
  
  ${ClassCard}:hover & {
    opacity: 0.2;
  }
`;

const CardContent = styled.div`
  position: relative;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 1;
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ $color }) => `${$color}20` || 'rgba(229, 168, 0, 0.1)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  transition: all 0.3s ease;

  svg {
    font-size: 1.5rem;
    color: ${({ $color }) => $color || 'var(--primary-color)'};
  }

  ${ClassCard}:hover & {
    transform: scale(1.1);
    animation: ${float} 2s ease-in-out infinite;
  }
`;

const ClassName = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
`;

const Schedule = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 15px;
`;

const TimesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const TimeTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 10px 15px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  transition: all 0.3s ease;

  .fire-icon {
    color: var(--primary-color);
    font-size: 0.8rem;
  }

  ${ClassCard}:hover & {
    background: rgba(229, 168, 0, 0.1);
    border-color: rgba(229, 168, 0, 0.3);
  }
`;

const CardBorder = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${({ $color }) => $color || 'var(--primary-color)'};
  transform: scaleX(0);
  transition: transform 0.4s ease;
  
  ${ClassCard}:hover & {
    transform: scaleX(1);
  }
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 25px;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease 0.8s forwards;
  `}
`;

const CTAText = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.3rem;
  color: #888;
`;

const CTAButton = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  color: #000;
  padding: 18px 45px;
  border-radius: 50px;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 1px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px var(--primary-glow);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 50px var(--primary-glow);
  }

  span {
    position: relative;
    z-index: 2;
  }
`;

const ButtonShine = styled.div`
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: ${shine} 3s ease-in-out infinite;
`;
